import moment from 'moment'

// HO-TASK-289: datos simulados del panel de root, con la forma EXACTA de las
// respuestas del backend (RewardConfigController, RewardReportController,
// RewardPartnerController y RewardAcceleratorController). Solo se usan en
// desarrollo (ver helpers/api/rewardsAdmin.js). Guardan estado en memoria para
// poder probar crear/editar sin backend; se reinicia al recargar la página.
//
// Escenarios con localStorage.rewardsMockScenario:
//   - 'default'   datos normales
//   - 'uncovered' el Fondo de Puntos no cubre el pasivo y la conciliación falla
//   - 'error'     todos los endpoints fallan

const delay = (ms = 450) => new Promise((resolve) => setTimeout(resolve, ms))

const getScenario = () => {
  try {
    return window.localStorage.getItem('rewardsMockScenario') || 'default'
  } catch {
    return 'default'
  }
}

const fail = async (status = 500, data = { message: 'Server Error' }) => {
  await delay()
  const error = { status, ...data }
  throw error
}

const guard = () => (getScenario() === 'error' ? fail() : null)

const today = () => moment().format('YYYY-MM-DD')

const DESCRIPTIONS = {
  xp_per_usd: 'XP acreditados por cada 1 USD pagado via pasarela',
  hc_per_usd: 'Homelan Coins acreditados por cada 1 USD pagado via pasarela (antes del multiplicador de nivel)',
  hc_per_usd_redemption: 'Homelan Coins necesarios para canjear 1 USD de valor',
  streak_bonus_min_months: 'Meses consecutivos pagando puntual y completo para activar el bonus de racha sobre HC',
  streak_bonus_rate: 'Bonus de racha sobre el multiplicador de HC una vez alcanzada la racha minima',
  punctuality_day_limit: 'El pago recibe bono de puntualidad si su fecha es anterior a este dia del mes del periodo',
  punctuality_bonus_xp: 'XP del bono de puntualidad',
  punctuality_bonus_hc: 'Homelan Coins del bono de puntualidad (no se multiplica)',
  commission_rate: 'Comision de Homelan sobre el monto pagado por el propietario',
  commission_min_usd: 'Comision minima en USD por transaccion',
  commission_max_usd: 'Comision maxima en USD por transaccion',
  points_fund_commission_share: 'Porcion de la comision neta que se reserva al Fondo de Puntos',
  prize_fund_collection_share: 'Porcion del ingreso de Homelan por comision que se reserva al Fondo de Premios',
  redemption_min_hc: 'Homelan Coins minimos por canje',
  redemption_max_hc: 'Homelan Coins maximos por canje; 0 significa sin tope',
  referral_hc: 'Homelan Coins que gana el referidor cuando su referido califica',
  referral_raffle_tickets: 'Tickets extra de sorteo por referido calificado',
  referral_monthly_cap: 'Referidos premiados como maximo por propietario y mes; 0 significa sin tope',
  fund_coverage_threshold: 'Cobertura minima del pasivo de HC por el Fondo de Puntos; 1 equivale al 100%',
  target_cost_per_transaction_usd: 'Costo objetivo del programa por transaccion, en USD'
}

// Valores de RewardRulesSeeder.
const SEED_VALUES = {
  xp_per_usd: 10,
  hc_per_usd: 1,
  hc_per_usd_redemption: 100,
  streak_bonus_min_months: 3,
  streak_bonus_rate: 0.2,
  punctuality_day_limit: 5,
  punctuality_bonus_xp: 100,
  punctuality_bonus_hc: 10,
  commission_rate: 0.02,
  commission_min_usd: 0.2,
  commission_max_usd: 1,
  points_fund_commission_share: 0.25,
  prize_fund_collection_share: 0.1,
  redemption_min_hc: 500,
  redemption_max_hc: 40000,
  referral_hc: 1000,
  referral_raffle_tickets: 5,
  referral_monthly_cap: 3,
  fund_coverage_threshold: 1,
  target_cost_per_transaction_usd: 0.2
}

let nextId = 100
const newId = () => ++nextId

const state = {
  rules: Object.entries(SEED_VALUES).map(([key, value], index) => ({
    id: index + 1,
    key,
    value,
    valid_from: '2026-09-01',
    valid_to: null,
    description: DESCRIPTIONS[key]
  })),
  levels: [
    { id: 1, name: 'Bronce', min_xp: 0, max_xp: 4999, coin_multiplier: 1, order: 1, badge_icon: 'bronze' },
    { id: 2, name: 'Plata', min_xp: 5000, max_xp: 19999, coin_multiplier: 1.1, order: 2, badge_icon: 'silver' },
    { id: 3, name: 'Oro', min_xp: 20000, max_xp: null, coin_multiplier: 1.25, order: 3, badge_icon: 'gold' }
  ],
  audits: [
    {
      id: 3,
      key: 'commission_rate',
      action: 'created',
      previous_value: 0.025,
      new_value: 0.02,
      valid_from: '2026-09-01',
      valid_to: null,
      note: 'Ajuste a la tarifa de lanzamiento',
      changed_by: 'root@homelan.app',
      changed_at: '2026-08-31T15:20:00-04:00'
    },
    {
      id: 2,
      key: 'commission_rate',
      action: 'closed',
      previous_value: 0.025,
      new_value: 0.025,
      valid_from: '2026-06-01',
      valid_to: '2026-08-31',
      note: 'Ajuste a la tarifa de lanzamiento',
      changed_by: 'root@homelan.app',
      changed_at: '2026-08-31T15:20:00-04:00'
    },
    {
      id: 1,
      key: 'level:Oro',
      action: 'level_updated',
      previous_value: 1.2,
      new_value: 1.25,
      valid_from: null,
      valid_to: null,
      note: 'min_xp 20000 -> 20000, max_xp sin tope -> sin tope',
      changed_by: 'root@homelan.app',
      changed_at: '2026-08-20T10:05:00-04:00'
    }
  ],
  partners: [
    { id: 1, name: 'Seguros Horizonte', slug: 'seguros-horizonte', logo: null, affiliate_commission_rate: 0.08, status: 'active' },
    { id: 2, name: 'Limpieza Express', slug: 'limpieza-express', logo: null, affiliate_commission_rate: 0.05, status: 'active' },
    { id: 3, name: 'Encuestas Plus', slug: 'encuestas-plus', logo: null, affiliate_commission_rate: 0, status: 'inactive' }
  ],
  accelerators: [
    {
      id: 1,
      partner_id: 1,
      code: 'poliza-hogar',
      type: 'insurance',
      name: 'Póliza de hogar',
      description: 'Contrata la póliza de hogar y gana Coins.',
      hc_amount: 1500,
      raffle_tickets: 3,
      monthly_cap_per_user: 1,
      valid_from: '2026-09-01',
      valid_to: '2026-12-31',
      is_active: true
    },
    {
      id: 2,
      partner_id: 2,
      code: 'limpieza-profunda',
      type: 'service',
      name: 'Limpieza profunda',
      description: null,
      hc_amount: 400,
      raffle_tickets: 1,
      monthly_cap_per_user: 2,
      valid_from: '2026-09-01',
      valid_to: null,
      is_active: true
    },
    {
      id: 3,
      partner_id: 3,
      code: 'encuesta-servicios',
      type: 'survey',
      name: 'Encuesta de servicios',
      description: 'Responde la encuesta del mes.',
      hc_amount: 50,
      raffle_tickets: 0,
      monthly_cap_per_user: null,
      valid_from: '2026-07-01',
      valid_to: '2026-08-31',
      is_active: false
    }
  ]
}

const activeRule = (key, day = today()) =>
  state.rules
    .filter((rule) => rule.key === key && rule.valid_from <= day && (!rule.valid_to || rule.valid_to >= day))
    .sort((a, b) => (a.valid_from < b.valid_from ? 1 : -1))[0]

const ruleValue = (key, day) => {
  const rule = activeRule(key, day)
  return rule ? Number(rule.value) : null
}

const validationError = (errors) => fail(406, { code: 'H0013', errors })
const businessError = (message) => fail(422, { error: message })

// --- Configuración ---

export const mockConfig = async () => {
  await (guard() || delay())
  return {
    data: {
      rules: Object.keys(SEED_VALUES).map((key) => {
        const rule = activeRule(key)
        return {
          key,
          value: rule ? Number(rule.value) : null,
          valid_from: rule ? rule.valid_from : null,
          valid_to: rule ? rule.valid_to : null,
          description: rule ? rule.description : null,
          rule_id: rule ? rule.id : null
        }
      }),
      levels: state.levels.map((level) => ({ ...level }))
    }
  }
}

export const mockSaveRule = async ({ key, value, valid_from: validFrom, note }) => {
  await (guard() || delay())
  if (!(key in SEED_VALUES)) return businessError(`Parametro desconocido: ${key}.`)

  const from = validFrom || today()
  const shares = ['points_fund_commission_share', 'prize_fund_collection_share']
  if (shares.includes(key)) {
    const total = shares.reduce((sum, share) => sum + (share === key ? Number(value) : ruleValue(share, from) || 0), 0)
    if (total >= 1) {
      return businessError(
        `Ese cambio deja el margen de Homelan en cero o negativo: los aportes suman ${Math.round(total * 10000) / 100}% de la comision.`
      )
    }
  }

  const active = activeRule(key, from)
  if (active && active.valid_from >= from) {
    return businessError('Ya hay una version de ese parametro vigente desde esa fecha o despues: elige una fecha posterior.')
  }

  const previous = active ? Number(active.value) : null
  const changedAt = moment().format()
  if (active) {
    active.valid_to = moment(from).subtract(1, 'day').format('YYYY-MM-DD')
    state.audits.unshift({
      id: newId(),
      key,
      action: 'closed',
      previous_value: previous,
      new_value: previous,
      valid_from: active.valid_from,
      valid_to: active.valid_to,
      note: note || null,
      changed_by: 'root@homelan.app',
      changed_at: changedAt
    })
  }

  const rule = { id: newId(), key, value: Number(value), valid_from: from, valid_to: null, description: DESCRIPTIONS[key] }
  state.rules.push(rule)
  state.audits.unshift({
    id: newId(),
    key,
    action: 'created',
    previous_value: previous,
    new_value: Number(value),
    valid_from: from,
    valid_to: null,
    note: note || null,
    changed_by: 'root@homelan.app',
    changed_at: changedAt
  })

  return { code: 'H0001', data: { id: rule.id, key, value: rule.value, valid_from: from } }
}

export const mockRuleHistory = async (key) => {
  await (guard() || delay(300))
  return {
    data: state.rules
      .filter((rule) => rule.key === key)
      .sort((a, b) => (a.valid_from < b.valid_from ? 1 : -1))
      .map(({ id, value, valid_from: validFrom, valid_to: validTo }) => ({ id, value, valid_from: validFrom, valid_to: validTo }))
  }
}

export const mockConfigAudits = async (key, limit = 50) => {
  await (guard() || delay(300))
  return { data: state.audits.filter((audit) => !key || audit.key === key).slice(0, limit) }
}

export const mockUpdateLevel = async (levelId, body) => {
  await (guard() || delay())
  const level = state.levels.find((item) => item.id === Number(levelId))
  if (!level) return fail(404, { code: 'H0014' })
  if (body.coin_multiplier !== undefined && Number(body.coin_multiplier) < 1) {
    return validationError({ coin_multiplier: ['El campo coin multiplier debe ser al menos 1.'] })
  }
  if (body.max_xp !== null && body.max_xp !== undefined && Number(body.max_xp) < Number(body.min_xp)) {
    return businessError('El XP maximo del nivel no puede ser menor que el minimo.')
  }

  const previous = level.coin_multiplier
  const before = `min_xp ${level.min_xp} -> ${body.min_xp}, max_xp ${level.max_xp ?? 'sin tope'} -> ${body.max_xp ?? 'sin tope'}`
  Object.assign(level, {
    min_xp: Number(body.min_xp),
    max_xp: body.max_xp === null ? null : Number(body.max_xp),
    coin_multiplier: Number(body.coin_multiplier)
  })
  state.audits.unshift({
    id: newId(),
    key: `level:${level.name}`,
    action: 'level_updated',
    previous_value: previous,
    new_value: level.coin_multiplier,
    valid_from: null,
    valid_to: null,
    note: body.note || before,
    changed_by: 'root@homelan.app',
    changed_at: moment().format()
  })
  return { data: { ...level } }
}

const truncate = (value) => Math.trunc(value * 100) / 100

// Misma cuenta que RewardConfigService::simulate.
export const mockSimulate = async ({ users, average_fee_usd: fee, overrides = {} }) => {
  await (guard() || delay(350))
  const value = (key, fallback) => (key in overrides ? Number(overrides[key]) : ruleValue(key) ?? fallback)

  let commissionPerUser = fee * value('commission_rate', 0)
  commissionPerUser = Math.max(commissionPerUser, value('commission_min_usd', 0))
  commissionPerUser = Math.min(commissionPerUser, value('commission_max_usd', Number.MAX_VALUE))

  const collection = truncate(users * fee)
  const revenue = truncate(users * commissionPerUser)
  const pointsShare = value('points_fund_commission_share', 0)
  const prizeShare = value('prize_fund_collection_share', 0)
  const pointsFund = truncate(revenue * pointsShare)
  const prizeFund = truncate(revenue * prizeShare)
  const netMargin = truncate(revenue - pointsFund - prizeFund)
  const hcIssued = truncate(users * fee * value('hc_per_usd', 0))
  const redemptionRate = value('hc_per_usd_redemption', 100)
  const liability = redemptionRate > 0 ? truncate(hcIssued / redemptionRate) : 0

  return {
    data: {
      users,
      average_fee_usd: fee,
      collection_usd: collection,
      commission_per_user_usd: truncate(commissionPerUser),
      gateway_revenue_usd: revenue,
      points_fund_usd: pointsFund,
      prize_fund_usd: prizeFund,
      net_margin_usd: netMargin,
      net_margin_percent: revenue > 0 ? Math.round((netMargin / revenue) * 10000) / 100 : 0,
      hc_issued: hcIssued,
      hc_liability_usd: liability,
      liability_covered_by_points_fund: pointsFund >= liability,
      shares_total: Math.round((pointsShare + prizeShare) * 1000000) / 1000000
    }
  }
}

// --- Reporte ---

// Números estables por mes para que el gráfico de evolución sea creíble.
const monthFactor = (date) => {
  const month = moment(date).month() + 1
  return 0.7 + ((month * 37) % 11) / 20
}

export const mockReport = async ({ start_date: startDate, end_date: endDate, condominium_id: condominiumId }) => {
  await (guard() || delay(600))
  if (!startDate || !endDate) {
    return validationError({ start_date: ['El campo start date es obligatorio.'] })
  }
  if (endDate < startDate) {
    return validationError({ end_date: ['El campo end date debe ser una fecha posterior o igual a start date.'] })
  }

  const uncovered = getScenario() === 'uncovered'
  const months = Math.max(1, moment(endDate).diff(moment(startDate), 'months') + 1)
  const factor = monthFactor(startDate) * months * (condominiumId ? 0.08 : 1)

  const transactions = Math.round(1240 * factor)
  const collection = truncate(transactions * 41.3)
  const commission = truncate(transactions * 0.83)
  const pointsContributions = truncate(commission * 0.25)
  const prizeContributions = truncate(commission * 0.1)
  const programCost = truncate(pointsContributions + prizeContributions)
  const netMargin = truncate(commission - programCost)
  const hcIssued = truncate(collection * 1.18)
  const hcRedeemed = truncate(hcIssued * 0.31)
  const hcReversed = truncate(hcIssued * 0.01)
  const liability = uncovered ? 3120.5 : 1840.25
  const pointsBalance = uncovered ? 2410.8 : 2575.4
  const ratio = Math.round((pointsBalance / liability) * 10000) / 10000

  return {
    data: {
      period: {
        from: startDate,
        to: endDate,
        date_basis: 'Fecha de registro del movimiento, para que los totales cuadren con los tres ledgers.'
      },
      condominium_id: condominiumId ? Number(condominiumId) : null,
      scope_note: condominiumId
        ? 'Los saldos y movimientos de los fondos son globales de Homelan: no se filtran por condominio.'
        : null,
      revenue: {
        collection_usd: collection,
        transactions,
        gateway_commission_usd: commission,
        affiliate_commission_usd: truncate(58.4 * factor),
        affiliate_events: Math.round(42 * factor)
      },
      funds: {
        points_contributions_usd: pointsContributions,
        points_withdrawals_usd: truncate(hcRedeemed / 100),
        prize_contributions_usd: prizeContributions,
        prizes_paid_usd: 0,
        points_balance_usd: pointsBalance,
        prize_balance_usd: 912.35
      },
      coins: {
        hc_issued: hcIssued,
        hc_redeemed: hcRedeemed,
        hc_reversed: hcReversed,
        hc_in_circulation: truncate(liability * 100),
        xp_issued: truncate(collection * 10.9)
      },
      liability: {
        hc_liability_usd: liability,
        coverage_ratio: ratio,
        threshold: 1,
        covered: ratio >= 1,
        shortfall_usd: ratio >= 1 ? 0 : truncate(liability - pointsBalance)
      },
      cost: {
        program_cost_usd: programCost,
        cost_per_transaction_usd: transactions > 0 ? truncate(programCost / transactions) : 0,
        target_cost_per_transaction_usd: 0.2,
        net_margin_usd: netMargin,
        net_margin_percent: commission > 0 ? Math.round((netMargin / commission) * 10000) / 100 : 0
      },
      reconciliation: {
        wallets_balance_hc: truncate(liability * 100),
        points_ledger_balance_hc: truncate(liability * 100) + (uncovered ? 125 : 0),
        wallets_match_ledger: !uncovered,
        points_fund_balance_usd: pointsBalance,
        points_fund_ledger_usd: pointsBalance,
        points_fund_matches_ledger: true,
        prize_fund_balance_usd: 912.35,
        prize_fund_ledger_usd: 912.35,
        prize_fund_matches_ledger: true,
        balanced: !uncovered
      },
      raffles: {
        available: false,
        note: 'El detalle auditable de sorteos depende de HO-TASK-273 y HO-TASK-274, que estan en el backlog.',
        prizes_paid_usd: 0
      }
    }
  }
}

export const mockExportReport = async ({ start_date: startDate, end_date: endDate, format }) => {
  await (guard() || delay(500))
  const lines = [
    'Programa Homelan Rewards',
    `Periodo,${startDate} a ${endDate}`,
    'Datos simulados de desarrollo (HO-TASK-289)'
  ]
  return new Blob([lines.join('\n')], { type: format === 'csv' ? 'text/csv' : 'text/plain' })
}

// --- Aliados y aceleradores ---

const partnerResource = (partner) => ({
  ...partner,
  accelerators_count: state.accelerators.filter((item) => item.partner_id === partner.id).length
})

const acceleratorResource = (accelerator) => {
  const partner = state.partners.find((item) => item.id === accelerator.partner_id)
  const { partner_id: _partnerId, ...rest } = accelerator
  return {
    ...rest,
    partner: partner ? { id: partner.id, name: partner.name, slug: partner.slug, logo: partner.logo } : null
  }
}

const randomSecret = () =>
  Array.from({ length: 60 }, () => 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'[Math.floor(Math.random() * 62)]).join('')

export const mockPartners = async () => {
  await (guard() || delay())
  return { data: [...state.partners].sort((a, b) => a.name.localeCompare(b.name)).map(partnerResource) }
}

export const mockCreatePartner = async (body) => {
  await (guard() || delay())
  if (state.partners.some((partner) => partner.slug === body.slug)) {
    return validationError({ slug: ['El valor del campo slug ya está en uso.'] })
  }
  const partner = {
    id: newId(),
    name: body.name,
    slug: body.slug,
    logo: body.logo || null,
    affiliate_commission_rate: Number(body.affiliate_commission_rate),
    status: body.status || 'active'
  }
  state.partners.push(partner)
  return { code: 'H0001', data: partnerResource(partner), webhook_secret: randomSecret() }
}

export const mockUpdatePartner = async (partnerId, body) => {
  await (guard() || delay())
  const partner = state.partners.find((item) => item.id === Number(partnerId))
  if (!partner) return fail(404, { code: 'H0014' })
  const { slug: _slug, ...editable } = body
  Object.assign(partner, editable, { affiliate_commission_rate: Number(body.affiliate_commission_rate) })
  return { data: partnerResource(partner) }
}

export const mockRotatePartnerSecret = async () => {
  await (guard() || delay())
  return { code: 'H0004', webhook_secret: randomSecret() }
}

export const mockDeletePartner = async (partnerId) => {
  await (guard() || delay())
  state.partners = state.partners.filter((item) => item.id !== Number(partnerId))
  return { code: 'H0004' }
}

export const mockAccelerators = async () => {
  await (guard() || delay())
  return {
    data: [...state.accelerators]
      .sort((a, b) => a.partner_id - b.partner_id || a.code.localeCompare(b.code))
      .map(acceleratorResource)
  }
}

export const mockCreateAccelerator = async (body) => {
  await (guard() || delay())
  const partnerId = Number(body.partner_id)
  if (state.accelerators.some((item) => item.partner_id === partnerId && item.code === body.code)) {
    return fail(409, { code: 'H0011' })
  }
  const accelerator = {
    id: newId(),
    ...body,
    partner_id: partnerId,
    hc_amount: Number(body.hc_amount),
    raffle_tickets: Number(body.raffle_tickets) || 0,
    is_active: body.is_active !== false
  }
  state.accelerators.push(accelerator)
  return { data: acceleratorResource(accelerator) }
}

export const mockUpdateAccelerator = async (acceleratorId, body) => {
  await (guard() || delay())
  const accelerator = state.accelerators.find((item) => item.id === Number(acceleratorId))
  if (!accelerator) return fail(404, { code: 'H0014' })
  Object.assign(accelerator, body, { hc_amount: Number(body.hc_amount), raffle_tickets: Number(body.raffle_tickets) || 0 })
  return { data: acceleratorResource(accelerator) }
}

export const mockDeleteAccelerator = async (acceleratorId) => {
  await (guard() || delay())
  const accelerator = state.accelerators.find((item) => item.id === Number(acceleratorId))
  if (accelerator) accelerator.is_active = false
  return { code: 'H0004' }
}
